import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../shared/permissions';
import { useSidebar } from '../shared/SidebarContext';
import { useCompany } from '../shared/CompanyContext';
import { useModules } from '../shared/ModulesContext';
import { IconLibrary } from '@tabler/icons-react';
import './Sidebar.css';

// ─── SVG Icon helpers (inline Tabler paths) ────────────────────────────────
const Icon = ({ d, d2 }: { d: string | string[]; d2?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="sidebar-icon" aria-hidden="true">
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

// ─── Nav item ──────────────────────────────────────────────────────────────
interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  collapsed: boolean;
  onClick?: () => void;
  isSubItem?: boolean;
  /** Extra path prefixes that should also count as active, for detail routes whose URL doesn't nest under `to` (e.g. singular "/stock/:id" vs the plural "/stocks" list). */
  extraMatch?: string[];
}

const NavItem = ({ to, label, icon, collapsed, onClick, isSubItem, extraMatch }: NavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to) || !!extraMatch?.some(prefix => location.pathname.startsWith(prefix));

  return (
    <li className={`sidebar-nav-item${isActive ? ' active' : ''}${isSubItem ? ' sub-item' : ''}`}>
      <Link
        className="sidebar-nav-link"
        to={to}
        title={collapsed ? label : undefined}
        onClick={onClick}
      >
        {icon}
        <span className="sidebar-nav-label">{label}</span>
      </Link>
    </li>
  );
};

// ─── Section divider ───────────────────────────────────────────────────────
const SectionLabel = ({ label, collapsed }: { label: string; collapsed: boolean }) => (
  <li className="sidebar-section-label" aria-hidden={collapsed || undefined}>
    {!collapsed && <span>{label}</span>}
    {collapsed && <span className="sidebar-section-dot" />}
  </li>
);

// ─── Sidebar ───────────────────────────────────────────────────────────────
const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin, hasPerm } = usePermissions();
  const { collapsed, mobileOpen, toggleCollapsed, closeMobile } = useSidebar();
  const { companyLogo, companyLogoDark, companyName } = useCompany();
  const { hasModule } = useModules();

  const [theme] = React.useState(
    () => document.documentElement.getAttribute('data-bs-theme') || 'light'
  );

  // Default open warehouses accordion when on a warehouses route
  const [warehousesOpen, setWarehousesOpen] = useState(
    () => location.pathname.startsWith('/warehouses')
  );

  const activeLogo = theme === 'dark'
    ? (companyLogoDark || companyLogo || '/static/img/warehouse.svg')
    : (companyLogo || '/static/img/warehouse.svg');

  const handleMobileClick = () => closeMobile();

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={closeMobile} aria-hidden="true" />
      )}

      <aside
        className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}${mobileOpen ? ' app-sidebar--mobile-open' : ''}`}
        data-bs-theme="dark"
      >
        {/* ── Logo ──────────────────────────────────────────────────── */}
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo" onClick={handleMobileClick}>
            <img src={activeLogo} alt={companyName} className="sidebar-logo-img" />
          </Link>
          <button
            className="sidebar-collapse-btn d-none d-lg-flex"
            onClick={toggleCollapsed}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              {collapsed
                ? <><path d="M9 6l6 6l-6 6" /></>
                : <><path d="M15 6l-6 6l6 6" /></>
              }
            </svg>
          </button>
        </div>

        {/* ── Navigation ────────────────────────────────────────────── */}
        <nav className="sidebar-nav">
          <ul className="sidebar-nav-list">

            <SectionLabel label={t('sidebar.sections.overview')} collapsed={collapsed} />

            {/* Dashboard */}
            <NavItem
              to="/dashboard"
              label={t('components.nav.dashboard')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M12 13m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0', 'M13.45 11.55l2.05 -2.05', 'M6.4 20a9 9 0 1 1 11.2 0z']} />}
            />

            {/* Projects */}
            {hasPerm('projects.view_project') && hasModule('projects') && (
              <NavItem
                to="/projects"
                label={t('components.nav.projects')}
                collapsed={collapsed}
                onClick={handleMobileClick}
                icon={<Icon d={['M3 4l18 0', 'M4 4v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-10', 'M12 16l0 4', 'M9 20l6 0', 'M8 12l3 -3l2 2l3 -3']} />}
              />
            )}

            {/* Contracts */}
            {hasPerm('contracts.view_contract') && hasModule('rentals') && (
              <NavItem
                to="/contracts"
                label={t('components.nav.contracts')}
                collapsed={collapsed}
                onClick={handleMobileClick}
                icon={<Icon d={['M8 21h-2a3 3 0 0 1 -3 -3v-1h5.5', 'M17 8.5v-3.5a2 2 0 1 1 2 2h-2', 'M19 3h-11a3 3 0 0 0 -3 3v11', 'M9 7h4', 'M9 11h4', 'M18.42 12.61a2.1 2.1 0 0 1 2.97 2.97l-6.39 6.42h-3v-3z']} />}
              />
            )}

            {/* Rentals */}
            {hasModule('rentals') && (
            <NavItem
              to="/rentals"
              label={t('components.nav.rentals')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8', 'M14 19l2 2l4 -4', 'M9 8h4', 'M9 12h2']} />}
            />
            )}

            <SectionLabel label={t('sidebar.sections.inventory')} collapsed={collapsed} />

            {/* Warehouses — accordion */}
            {hasModule('warehouses') && (
            <li className={`sidebar-nav-item sidebar-nav-accordion${location.pathname.startsWith('/warehouses') ? ' active' : ''}`}>
              <button
                className="sidebar-nav-link sidebar-nav-accordion-toggle"
                title={collapsed ? t('components.nav.warehouses') : undefined}
                onClick={() => {
                  if (collapsed) {
                    // navigate to main warehouses page; let NavItem handle active state
                    window.location.href = '/warehouses/products';
                  } else {
                    setWarehousesOpen((o) => !o);
                  }
                }}
                aria-expanded={!collapsed && warehousesOpen}
              >
                <Icon d={['M3 21v-13l9 -4l9 4v13', 'M13 13h4v8h-10v-6h6', 'M13 21v-9a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v3']} />
                <span className="sidebar-nav-label">{t('components.nav.warehouses')}</span>
                {!collapsed && (
                  <svg className={`sidebar-accordion-chevron${warehousesOpen ? ' open' : ''}`}
                    xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M6 9l6 6l6 -6" />
                  </svg>
                )}
              </button>

              {!collapsed && warehousesOpen && (
                <ul className="sidebar-sub-nav">
                  <NavItem to="/warehouses/products" label={t('components.nav.catalog')} collapsed={false} onClick={handleMobileClick} isSubItem
                    icon={<IconLibrary size={18} className="sidebar-icon" aria-hidden="true" />}
                  />
                  <NavItem to="/warehouses/stocks"   label={t('components.nav.warehouseSpaces')} collapsed={false} onClick={handleMobileClick} isSubItem
                    extraMatch={['/warehouses/stock/']}
                    icon={<Icon d={['M4 21v-15c0 -1 1 -2 2 -2h5c1 0 2 1 2 2v15', 'M16 8h2c1 0 2 1 2 2v11', 'M3 21h18', 'M10 12v0', 'M10 16v0', 'M10 8v0', 'M7 12v0', 'M7 16v0', 'M7 8v0', 'M17 12v0', 'M17 16v0']} />}
                  />
                  <NavItem to="/warehouses/bundles"  label={t('components.nav.bundles')}         collapsed={false} onClick={handleMobileClick} isSubItem
                    icon={<Icon d={['M21 16.008v-8.018a1.98 1.98 0 0 0 -1 -1.717l-7 -4.008a2.016 2.016 0 0 0 -2 0l-7 4.008a1.98 1.98 0 0 0 -1 1.717v8.018a1.98 1.98 0 0 0 1 1.717l7 4.008a2.016 2.016 0 0 0 2 0l7 -4.008a1.98 1.98 0 0 0 1 -1.717z', 'M12 22v-10', 'M12 12l8.73 -5.04', 'M3.27 6.96l8.73 5.04']} />}
                  />
                  <NavItem to="/warehouses/cases"    label={t('components.nav.cases')}           collapsed={false} onClick={handleMobileClick} isSubItem
                    icon={<Icon d={['M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z', 'M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2', 'M12 12l0 .01', 'M3 13a20 20 0 0 0 18 0']} />}
                  />
                  <NavItem to="/warehouses/suppliers" label={t('components.nav.suppliers')}      collapsed={false} onClick={handleMobileClick} isSubItem
                    icon={<Icon d={['M3 21l18 0', 'M5 21v-14l8 -4v18', 'M19 21v-10l-6 -4', 'M9 9l0 .01', 'M9 12l0 .01', 'M9 15l0 .01', 'M9 18l0 .01']} />}
                  />
                </ul>
              )}
            </li>
            )}

            {/* Invoices */}
            {hasModule('invoices') && (
            <NavItem
              to="/invoices"
              label={t('components.nav.invoices')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M14 3v4a1 1 0 0 0 1 1h4', 'M19 12v7a1.78 1.78 0 0 1 -3.1 1.4a1.65 1.65 0 0 0 -2.6 0a1.65 1.65 0 0 1 -2.6 0a1.65 1.65 0 0 0 -2.6 0a1.78 1.78 0 0 1 -3.1 -1.4v-14a2 2 0 0 1 2 -2h7l5 5v4.25']} />}
            />
            )}

            <SectionLabel label={t('sidebar.sections.people')} collapsed={collapsed} />

            {/* Customers */}
            <NavItem
              to="/customers"
              label={t('components.nav.customers')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0', 'M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2']} />}
            />

            {/* Crew */}
            <NavItem
              to="/crew"
              label={t('components.nav.team')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0', 'M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1', 'M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0', 'M17 10h2a2 2 0 0 1 2 2v1', 'M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0', 'M3 13v-1a2 2 0 0 1 2 -2h2']} />}
            />

            {/* Transport */}
            <NavItem
              to="/transport"
              label={t('components.nav.transport')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M5 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0', 'M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0', 'M7 18h8m4 0h2v-6a5 7 0 0 0 -5 -7h-1l1.5 7h4.5', 'M12 18v-13h3', 'M3 17l0 -5l9 0']} />}
            />

            {/* Chat */}
            {hasModule('ai-assistant') && (
            <NavItem
              to="/chat"
              label={t('chat.title')}
              collapsed={collapsed}
              onClick={handleMobileClick}
              icon={<Icon d={['M21 14l-3 -3h-7a1 1 0 0 1 -1 -1v-6a1 1 0 0 1 1 -1h9a1 1 0 0 1 1 1v10', 'M14 15v2a1 1 0 0 1 -1 1h-7l-3 3v-10a1 1 0 0 1 1 -1h2']} />}
            />
            )}

            {/* Support */}
            {hasPerm('support.view_ticket') && (
              <NavItem
                to="/support"
                label={t('support.title', 'Support')}
                collapsed={collapsed}
                onClick={handleMobileClick}
                icon={<Icon d={['M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0', 'M12 8v4', 'M12 16h.01']} />}
              />
            )}

            {/* Admin section */}
            {isAdmin && (
              <>
                <SectionLabel label={t('sidebar.sections.admin')} collapsed={collapsed} />
                <NavItem
                  to="/settings"
                  label={t('menu.settings')}
                  collapsed={collapsed}
                  onClick={handleMobileClick}
                  icon={<Icon d={['M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z', 'M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0']} />}
                />
                <NavItem
                  to="/accounts"
                  label={t('components.nav.accounts')}
                  collapsed={collapsed}
                  onClick={handleMobileClick}
                  icon={<Icon d={['M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', 'M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0', 'M6.168 18.849a4 4 0 0 1 3.832 -2.849h4a4 4 0 0 1 3.834 2.855']} />}
                />
              </>
            )}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
